// mds-dojo — dijit/form/TextBox — FAITHFUL port of references/dijit/form/TextBox.js.
// Codemod pipeline; declare from the faithful runtime. Adaptations:
//  - `dojo/text!./templates/TextBox.html` → `import template from './templates/TextBox.html?raw'`
//    (template .html copied from references/dijit/form/templates/).
//  - `dojo/sniff` → `dojo/has` (has("ie")/has("mozilla") undefined under evergreen →
//    the IE<9 shim block and the mozilla branch never run).
//  - dojo-bidi dropped: class name loses "_NoBidi" (always "dijit.form.TextBox"); the
//    trailing has("dojo-bidi") re-declare removed.
//  - `dijit/main` is not a module here; the back-compat `dijit._setSelectionRange`
//    global (set in the dead IE<9 block) targets the published window.dijit namespace.
//  - `kernel.deprecated` shimmed to no-op when the slim mds kernel lacks it.
// A base class for textbox form inputs.
import declare from '../../dojo/_base/declare-runtime'; // declare
import domConstruct from '../../dojo/dom-construct'; // domConstruct.create
import domStyle from '../../dojo/dom-style'; // domStyle.getComputedStyle
import kernel from '../../dojo/_base/kernel'; // kernel.deprecated
import lang from '../../dojo/_base/lang'; // lang.hitch
import on from '../../dojo/on';
import has from '../../dojo/has'; // has("ie") has("mozilla")
import _FormValueWidget from './_FormValueWidget';
import _TextBoxMixin from './_TextBoxMixin';
import template from './templates/TextBox.html?raw';

// dijit/main namespace (for the back-compat static export in the IE<9 block below).
var dijit: any = (typeof window !== 'undefined' && (window as any).dijit) || {};
var deprecated: any = (kernel as any).deprecated || function () { /* no-op shim */ };

var TextBox: any = declare("dijit.form.TextBox", [_FormValueWidget, _TextBoxMixin], {
	// summary:
	//		A base class for textbox form inputs

	templateString: template,
	_singleNodeTemplate: '<input class="dijit dijitReset dijitLeft dijitInputField" data-dojo-attach-point="textbox,focusNode" autocomplete="off" type="${type}" ${!nameAttrSetting} />',

	_buttonInputDisabled: has("ie") ? "disabled" : "", // allows IE to disallow focus, but Firefox cannot be disabled for mousedown events

	baseClass: "dijitTextBox",

	postMixInProperties: function () {
		var type = this.type.toLowerCase();
		if (this.templateString && this.templateString.toLowerCase() == "input" || ((type == "hidden" || type == "file") && this.templateString == this.constructor.prototype.templateString)) {
			this.templateString = this._singleNodeTemplate;
		}
		this.inherited(arguments);
	},

	postCreate: function () {
		this.inherited(arguments);

		if (has("ie") < 9) {
			// IE INPUT tag fontFamily has to be set directly using STYLE
			// the defer gives IE a chance to render the TextBox and to deal with font inheritance
			this.defer(function (this: any) {
				try {
					var s = domStyle.getComputedStyle(this.domNode); // can throw an exception if widget is immediately destroyed
					if (s) {
						var ff = s.fontFamily;
						if (ff) {
							var inputs = this.domNode.getElementsByTagName("INPUT");
							if (inputs) {
								for (var i = 0; i < inputs.length; i++) {
									inputs[i].style.fontFamily = ff;
								}
							}
						}
					}
				} catch (e) {/*when used in a Dialog, and this is called before the dialog is
						 shown, s.fontFamily would trigger "Invalid Argument" error.*/}
			});
		}
	},

	_setPlaceHolderAttr: function (v: any) {
		this._set("placeHolder", v);
		if (!this._phspan) {
			this._attachPoints.push('_phspan');
			this._phspan = domConstruct.create('span', {
				// dijitInputField class gives placeHolder same padding as the input field
				className: 'dijitPlaceHolder dijitInputField'
			}, this.textbox, 'after');
			this.own(
				on(this._phspan, "mousedown", function (evt: any) { evt.preventDefault(); }),
				on(this._phspan, "touchend, pointerup, MSPointerUp", lang.hitch(this, function (this: any) {
					// If the user clicks placeholder rather than the <input>, need programmatic focus.
					this.focus();
				}))
			);
		}
		this._phspan.innerHTML = "";
		this._phspan.appendChild(this._phspan.ownerDocument.createTextNode(v));
		this._updatePlaceHolder();
	},

	_onInput: function (/*Event*/ evt: any) {
		// summary:
		//		Called AFTER the input event has happened
		//		See if the placeHolder text should be removed or added while editing.
		this.inherited(arguments);
		this._updatePlaceHolder();
	},

	_updatePlaceHolder: function () {
		if (this._phspan) {
			this._phspan.style.display = (this.placeHolder && !this.textbox.value) ? "" : "none";
		}
	},

	_setValueAttr: function (value: any, /*Boolean?*/ priorityChange?: any, /*String?*/ formattedValue?: any) {
		this.inherited(arguments);
		this._updatePlaceHolder();
	},

	getDisplayedValue: function () {
		// summary:
		//		Deprecated.  Use get('displayedValue') instead.
		deprecated(this.declaredClass + "::getDisplayedValue() is deprecated. Use get('displayedValue') instead.", "", "2.0");
		return this.get('displayedValue');
	},

	setDisplayedValue: function (/*String*/ value: any) {
		// summary:
		//		Deprecated.  Use set('displayedValue', ...) instead.
		deprecated(this.declaredClass + "::setDisplayedValue() is deprecated. Use set('displayedValue', ...) instead.", "", "2.0");
		this.set('displayedValue', value);
	},

	_onBlur: function (e: any) {
		if (this.disabled) { return; }
		this.inherited(arguments);
		this._updatePlaceHolder();

		if (has("mozilla")) {
			if (this.selectOnClick) {
				// clear selection so that the next mouse click doesn't reselect
				this.textbox.selectionStart = this.textbox.selectionEnd = undefined;
			}
		}
	},

	_onFocus: function (/*String*/ by: string) {
		if (this.disabled || this.readOnly) { return; }
		this.inherited(arguments);
		this._updatePlaceHolder();
	}
});

if (has("ie") < 9) {
	TextBox.prototype._isTextSelected = function (this: any) {
		var range = this.ownerDocument.selection.createRange();
		var parent = range.parentElement();
		return parent == this.textbox && range.text.length > 0;
	};

	// Overrides definition of _setSelectionRange from _TextBoxMixin (TODO: move to _TextBoxMixin.js?)
	dijit._setSelectionRange = _TextBoxMixin._setSelectionRange = function (/*DomNode*/ element: any, /*Number?*/ start: any, /*Number?*/ stop: any) {
		if (element.createTextRange) {
			var r = element.createTextRange();
			r.collapse(true);
			r.moveStart("character", -99999); // move to 0
			r.moveStart("character", start); // delta from 0 is the correct position
			r.moveEnd("character", stop - start);
			r.select();
		}
	}
}

export default TextBox;
